/* Decorative campaign motion only. No network, storage, or storefront actions. */
(() => {
  if (customElements.get('ss-team-reveal')) return;

  class SsTeamReveal extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.value = 0;
      this.target = 0;
      this.frame = 0;
      this.keyboardTeam = null;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.motion = matchMedia('(min-width: 1000px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)');
      this.updateBounds = () => { this.bounds = this.getBoundingClientRect(); };
      this.updateViewportOffset = () => {
        // Include the actual header and both announcement bars without reserving logo space.
        const top = Math.max(0, Math.round(this.getBoundingClientRect().top + window.scrollY));
        this.style.setProperty('--ss-hero-offset', `${top}px`);
      };
      this.onMotion = () => { this.updateViewportOffset(); this.reset(true); this.updateBounds(); };
      this.onPointer = event => {
        if (event.pointerType !== 'mouse' || !this.motion.matches || this.keyboardTeam) return;
        if (event.type === 'pointerenter') this.updateBounds();
        const position = Math.max(-1, Math.min(1, ((event.clientX - this.bounds.left) / this.bounds.width - 0.5) * 2));
        // A small quiet center makes the joined halves easy to hold and inspect.
        const direction = Math.sign(position) * Math.max(0, Math.abs(position) - 0.05) / 0.75;
        this.setTarget(Math.max(-1, Math.min(1, direction)));
      };
      this.onLeave = () => { if (!this.keyboardTeam) this.setTarget(0); };
      this.onFocus = event => {
        if (!this.motion.matches || !event.target.matches(':focus-visible')) return;
        const team = event.target.closest('.ss-team');
        if (!team) return;
        this.keyboardTeam = team;
        this.setTarget(team.classList.contains('ss-team--sense') ? -1 : 1);
      };
      this.onBlur = event => {
        if (this.keyboardTeam?.contains(event.relatedTarget)) return;
        this.keyboardTeam = null;
        this.setTarget(0);
      };
      this.tick = time => {
        this.frame = 0;
        if (!this.connected || !this.motion.matches || document.hidden) { this.reset(true); return; }
        const elapsed = Math.min(64, Math.max(0, time - (this.lastTime ?? time - 16)));
        this.lastTime = time;
        this.value += (this.target - this.value) * (1 - Math.exp(-elapsed / 140));
        const settled = Math.abs(this.target - this.value) < 0.001;
        if (settled) this.value = this.target;
        this.draw();
        if (settled) this.lastTime = null;
        else this.frame = requestAnimationFrame(this.tick);
      };
      this.addEventListener('pointerenter', this.onPointer, options);
      this.addEventListener('pointermove', this.onPointer, options);
      this.addEventListener('pointerleave', this.onLeave, options);
      this.addEventListener('pointercancel', this.onLeave, options);
      this.addEventListener('focusin', this.onFocus, options);
      this.addEventListener('focusout', this.onBlur, options);
      this.motion.addEventListener('change', this.onMotion, options);
      window.addEventListener('blur', this.onMotion, options);
      window.addEventListener('resize', this.onMotion, options);
      document.addEventListener('visibilitychange', this.onMotion, options);
      this.resize = new ResizeObserver(() => {
        this.updateViewportOffset();
        const bounds = this.getBoundingClientRect();
        // The observer's initial notification must not cancel a fresh hover.
        if (this.bounds && (bounds.width !== this.bounds.width || bounds.height !== this.bounds.height)) this.reset(true);
        this.bounds = bounds;
      });
      this.resize.observe(this);
      for (const element of document.querySelectorAll('.section-header, .section-announcement-bar')) this.resize.observe(element);
      const announcement = this.closest('.ss-destination')?.querySelector('.ss-announcement');
      if (announcement) this.resize.observe(announcement);
      this.updateViewportOffset();
      this.updateBounds();
      this.draw();
    }

    setTarget(value) {
      if (!this.motion.matches || document.hidden) { this.reset(true); return; }
      this.target = value;
      if (!this.frame && Math.abs(this.value - value) > 0.001) {
        this.lastTime = null;
        this.frame = requestAnimationFrame(this.tick);
      }
    }

    draw() {
      const senseRetreat = Math.max(0, this.value);
      const heartRetreat = Math.max(0, -this.value);
      this.style.setProperty('--ss-split', `${(50 - this.value * 26).toFixed(3)}%`);
      this.style.setProperty('--ss-sense-shift', `${(-senseRetreat * 44).toFixed(3)}px`);
      this.style.setProperty('--ss-heart-shift', `${(heartRetreat * 44).toFixed(3)}px`);
      for (const [team, retreat] of [['sense', senseRetreat], ['heart', heartRetreat]]) {
        this.style.setProperty(`--ss-${team}-scale`, (1 - retreat * 0.06).toFixed(4));
        this.style.setProperty(`--ss-${team}-opacity`, (1 - retreat * 0.38).toFixed(4));
      }
    }

    reset(immediate = false) {
      this.keyboardTeam = null;
      if (!immediate) { this.setTarget(0); return; }
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.lastTime = null;
      this.value = this.target = 0;
      this.draw();
    }

    disconnectedCallback() {
      this.connected = false;
      this.events?.abort();
      this.resize?.disconnect();
      this.reset(true);
    }
  }
  customElements.define('ss-team-reveal', SsTeamReveal);
})();
